/**
 * ft-consent-banner
 * Toast-style consent banner with progress bar - matches mockup exactly.
 *
 * Attributes:
 * - last-policy-update: Date when policies were last updated (YYYY-MM-DD)
 *
 * Events:
 * - ft-consent-accept: Fired when user acknowledges
 * - ft-consent-withdraw: Fired when user withdraws consent
 */

const CONSENT_KEY = 'ft-consent';

class FtConsentBanner extends HTMLElement {
  private abortController = new AbortController();

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback(): void {
    this.render();
    this.setupEventListeners();
    this.checkAndShowIfNeeded();
  }

  private checkAndShowIfNeeded(): void {
    const consent = localStorage.getItem(CONSENT_KEY);

    if (!consent) {
      this.show();
      return;
    }

    try {
      const { acceptedDate, timestamp } = JSON.parse(consent);
      const consentDate = acceptedDate ? new Date(acceptedDate) : new Date(timestamp);
      const policyDate = this.lastPolicyUpdate;

      if (policyDate && consentDate < policyDate) {
        this.show();
      }
    } catch {
      this.show();
    }
  }

  disconnectedCallback(): void {
    this.abortController.abort();
  }

  get lastPolicyUpdate(): Date | null {
    const attr = this.getAttribute('last-policy-update');
    if (!attr) return null;
    const date = new Date(attr + 'T00:00:00');
    return isNaN(date.getTime()) ? null : date;
  }

  private render(): void {
    const style = document.createElement('style');
    style.textContent = this.getStyles();

    const template = document.createElement('template');
    template.innerHTML = this.getTemplate();

    if (this.shadowRoot) {
      this.shadowRoot.innerHTML = '';
      this.shadowRoot.appendChild(style);
      this.shadowRoot.appendChild(template.content.cloneNode(true));
    }
  }

  private getStyles(): string {
    return `
      :host {
        --banner-bg: var(--surface, #f0f0f0);
        --banner-border: var(--divider-color, #E2E4E8);
        --banner-text: var(--text, #1C1C1E);
        --banner-muted: var(--muted, #555555);
        --banner-primary: var(--primary, #E0524D);
        --banner-primary-pressed: var(--primary-pressed, #D53A2A);
        --banner-radius: var(--radius, 8px);

        position: fixed;
        bottom: 24px;
        left: 50%;
        z-index: 9999;
        width: 480px;
        max-width: calc(100% - 48px);
        pointer-events: none;
      }

      @media (prefers-color-scheme: dark) {
        :host {
          --banner-bg: var(--surface, #14161a);
          --banner-border: var(--divider-color, #2a2d33);
          --banner-text: var(--text, #ffffff);
          --banner-muted: var(--muted, #a3a3a3);
        }
      }

      .banner {
        background: var(--banner-bg);
        border: 1px solid var(--banner-border);
        border-radius: var(--banner-radius);
        overflow: hidden;
        box-shadow:
          0 4px 16px rgba(0, 0, 0, 0.1),
          0 0 1px rgba(0, 0, 0, 0.1);
        pointer-events: auto;

        /* Hidden state */
        opacity: 0;
        transform: translateX(-50%) translateY(100px);
        transition:
          opacity 250ms ease-out,
          transform 400ms cubic-bezier(0.3, 0.7, 0.4, 1);
        will-change: transform, opacity;
      }

      :host([visible]) .banner {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
      }

      .content {
        padding: 16px 20px;
        display: flex;
        align-items: center;
        gap: 16px;
      }

      .text {
        flex: 1;
        min-width: 0;
      }

      .title {
        font: 600 13px/1.3 Inter, system-ui, sans-serif;
        color: var(--banner-text);
        margin: 0 0 2px;

        /* Staggered entrance */
        opacity: 0;
        transform: translateY(8px);
        transition:
          opacity 250ms ease-out 150ms,
          transform 250ms cubic-bezier(0.3, 0.7, 0.4, 1) 150ms;
      }

      :host([visible]) .title {
        opacity: 1;
        transform: translateY(0);
      }

      .description {
        font: 400 12px/1.4 Inter, system-ui, sans-serif;
        color: var(--banner-muted);
        margin: 0;

        /* Staggered entrance - more delay */
        opacity: 0;
        transform: translateY(8px);
        transition:
          opacity 250ms ease-out 200ms,
          transform 250ms cubic-bezier(0.3, 0.7, 0.4, 1) 200ms;
      }

      :host([visible]) .description {
        opacity: 1;
        transform: translateY(0);
      }

      .description a {
        color: var(--banner-muted);
        text-decoration: underline;
        text-underline-offset: 2px;
        transition: color 150ms ease;
      }

      .description a:hover {
        color: var(--banner-text);
      }

      .actions {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-shrink: 0;

        /* Staggered entrance */
        opacity: 0;
        transform: translateX(10px);
        transition:
          opacity 250ms ease-out 250ms,
          transform 250ms cubic-bezier(0.3, 0.7, 0.4, 1) 250ms;
      }

      :host([visible]) .actions {
        opacity: 1;
        transform: translateX(0);
      }

      .link {
        font: 500 12px/1 Inter, system-ui, sans-serif;
        color: var(--banner-muted);
        text-decoration: none;
        padding: 8px 12px;
        border-radius: 6px;
        transition: all 150ms ease;
      }

      .link:hover {
        color: var(--banner-text);
        background: var(--banner-border);
      }

      .link:focus-visible {
        outline: 2px solid var(--banner-text);
        outline-offset: 2px;
      }

      .btn {
        appearance: none;
        border: none;
        background: var(--banner-primary);
        color: white;
        font: 600 13px/1 Inter, system-ui, sans-serif;
        padding: 10px 18px;
        border-radius: 6px;
        cursor: pointer;
        will-change: transform;
        transition:
          background 150ms ease,
          transform 34ms;
      }

      .btn:hover {
        background: var(--banner-primary-pressed);
      }

      .btn:active {
        transform: translateY(1px);
      }

      .btn:focus-visible {
        outline: 2px solid var(--banner-text);
        outline-offset: 2px;
      }

      .btn-secondary {
        background: transparent;
        border: 1px solid var(--banner-border);
        color: var(--banner-text);
      }

      .btn-secondary:hover {
        background: var(--banner-border);
      }

      .btn-danger {
        background: transparent;
        border: 1px solid var(--banner-border);
        color: var(--banner-primary);
      }

      .btn-danger:hover {
        background: rgba(224, 82, 77, 0.1);
        border-color: var(--banner-primary);
      }

      /* Progress bar */
      .progress {
        height: 3px;
        background: var(--banner-border);
        position: relative;
        overflow: hidden;
      }

      .progress-bar {
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        background: var(--banner-primary);
        transform-origin: left;
        transform: scaleX(1);
      }

      :host([visible]) .progress-bar {
        animation: progress-shrink 8s linear forwards;
        animation-delay: 500ms;
      }

      @keyframes progress-shrink {
        from { transform: scaleX(1); }
        to { transform: scaleX(0); }
      }

      /* Responsive */
      @media (max-width: 560px) {
        :host {
          bottom: 16px;
          left: 16px;
          right: 16px;
          width: auto;
          max-width: none;
        }

        .banner {
          transform: translateY(100px);
        }

        :host([visible]) .banner {
          transform: translateY(0);
        }

        .content {
          flex-direction: column;
          align-items: stretch;
          gap: 12px;
          text-align: center;
        }

        .actions {
          justify-content: center;
        }
      }

      /* Reduced motion */
      @media (prefers-reduced-motion: reduce) {
        .banner,
        .title,
        .description,
        .actions {
          transition: none;
          transform: none;
          opacity: 1;
        }

        .progress-bar {
          animation: none;
          transform: scaleX(1);
        }

        :host(:not([visible])) .banner {
          display: none;
        }
      }
    `;
  }

  private getTemplate(): string {
    return `
      <div class="banner" role="dialog" aria-label="Terms and cookie consent">
        <div class="content">
          <div class="text">
            <p class="title">Not investment advice. Data may contain errors.</p>
            <p class="description">By continuing you agree to our <a href="/terms">terms</a>. We use <a href="/cookies">cookies</a>.</p>
          </div>
          <div class="actions" data-actions>
            <a href="/terms" class="link">Terms</a>
            <button class="btn" type="button" data-accept>Accept</button>
          </div>
        </div>
        <div class="progress">
          <div class="progress-bar"></div>
        </div>
      </div>
    `;
  }

  private setupEventListeners(): void {
    const signal = this.abortController.signal;

    // Accept button
    this.shadowRoot?.querySelector('[data-accept]')?.addEventListener('click', () => {
      this.accept();
    }, { signal });

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.hasAttribute('visible')) {
        this.hide();
      }
    }, { signal });

    // Listen for show request from footer
    window.addEventListener('ft-consent-banner-show', () => this.showForReview(), { signal });
  }

  private updateActionsForReview(): void {
    const actionsEl = this.shadowRoot?.querySelector('[data-actions]');
    if (!actionsEl) return;

    const consent = localStorage.getItem(CONSENT_KEY);
    const policyDate = this.lastPolicyUpdate;

    if (consent) {
      try {
        const { acceptedDate, timestamp } = JSON.parse(consent);
        const consentDate = acceptedDate ? new Date(acceptedDate) : new Date(timestamp);

        // Check if policy updated after consent
        const needsReAck = policyDate && consentDate < policyDate;

        if (needsReAck) {
          // Policy updated - show re-acknowledge button
          actionsEl.innerHTML = `
            <button class="btn btn-secondary" type="button" data-close>Close</button>
            <button class="btn" type="button" data-accept>Acknowledge Updates</button>
          `;
        } else {
          // Already consented - show close and withdraw options
          actionsEl.innerHTML = `
            <button class="btn btn-danger" type="button" data-withdraw>Withdraw</button>
            <button class="btn" type="button" data-close>Close</button>
          `;
        }

        this.setupDynamicListeners();
      } catch {
        // Parse error - show default
      }
    }
  }

  private setupDynamicListeners(): void {
    this.shadowRoot?.querySelector('[data-close]')?.addEventListener('click', () => this.hide());
    this.shadowRoot?.querySelector('[data-accept]')?.addEventListener('click', () => this.accept());
    this.shadowRoot?.querySelector('[data-withdraw]')?.addEventListener('click', () => this.withdraw());
  }

  public show(): void {
    // Reset to default template for fresh consent
    this.render();
    this.setupEventListeners();

    this.setAttribute('visible', '');
  }

  public showForReview(): void {
    // Show for reviewing existing consent
    this.render();
    this.setupEventListeners();
    this.updateActionsForReview();

    this.setAttribute('visible', '');
  }

  public hide(): void {
    this.removeAttribute('visible');
  }

  private accept(): void {
    const now = new Date();
    const acceptedDate = now.toISOString().split('T')[0];
    localStorage.setItem(CONSENT_KEY, JSON.stringify({
      accepted: true,
      timestamp: now.getTime(),
      acceptedDate
    }));

    this.dispatchEvent(new CustomEvent('ft-consent-accept', {
      bubbles: true,
      composed: true,
      detail: { timestamp: now.getTime(), acceptedDate }
    }));

    this.hide();
    window.dispatchEvent(new CustomEvent('ft-consent-updated'));
  }

  private withdraw(): void {
    localStorage.removeItem(CONSENT_KEY);

    this.dispatchEvent(new CustomEvent('ft-consent-withdraw', {
      bubbles: true,
      composed: true
    }));

    this.hide();
    window.dispatchEvent(new CustomEvent('ft-consent-updated'));
  }
}

if (!customElements.get('ft-consent-banner')) {
  customElements.define('ft-consent-banner', FtConsentBanner);
}
